import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../../config";
import {
    FIT_TONE, STATUS, dateTime, dayMonth, money, screenState, stateDetail,
} from "./campaignFormat";
import "../../../styles/Campaigns.css";

// One campaign: what it is running on, what is on those screens right now, and
// what it cost.
//
// **Everything on this page is derived, and it says so.** No device uploads a
// screenshot and none can be asked a question, so "Playing" means "an approved
// booking covers now, the screen is checking in, it is on air, and its host has
// not stopped it". The footer repeats that in words, because the failure this
// page could otherwise cause is an advertiser believing a screen is lit when
// nobody knows whether it is.
//
// Rows arrive from the API already sorted problems-first, and the order is kept
// rather than re-sorted here: the four rows visible before "show all" should be
// the ones worth looking at.
const REFRESH_MS = 30000;
const VISIBLE_SCREENS = 4;

export default function CampaignDetail() {
    const { campaignId } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [showAll, setShowAll] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [swapOpen, setSwapOpen] = useState(false);
    const [swapAdId, setSwapAdId] = useState("");
    const [busy, setBusy] = useState(false);
    const [swapResult, setSwapResult] = useState(null);

    const token = localStorage.getItem("token");
    const authHeaders = { Authorization: `Bearer ${token}` };

    const load = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/campaigns/${campaignId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.status === 404) throw new Error("That campaign was not found.");
            if (!res.ok) throw new Error("Could not load this campaign.");
            setCampaign(await res.json());
            setError("");
        } catch (err) {
            setError(err.message || "Could not load this campaign.");
        }
        setLoading(false);
    }, [campaignId, token]);

    useEffect(() => {
        load();
        const id = setInterval(load, REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/customer/ads`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => setAds(d.images || []))
            .catch(() => setAds([]));
    }, [token]);

    async function saveName() {
        const name = draftName.trim();
        if (!name) return;
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/campaigns/${campaignId}`, {
                method: "PATCH",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify({ name }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setCampaign((c) => ({ ...c, name: data.name }));
                setRenaming(false);
                setNotice("Renamed.");
            } else {
                setError(data.message || "Could not rename this campaign.");
            }
        } catch {
            setError("Network error. The name was not changed.");
        }
        setBusy(false);
    }

    async function swapCreative() {
        if (!swapAdId) { setError("Choose an ad first."); return; }
        setBusy(true);
        setError(""); setNotice(""); setSwapResult(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/campaigns/${campaignId}/creative`, {
                method: "POST",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify({ image_id: Number(swapAdId) }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                // The server's own summary: some screens switch now, the
                // approved ones wait for their host. Replacing that with
                // "Done" would promise a change that has not happened yet.
                setSwapResult(data);
                setNotice(data.message || "Requested.");
                setSwapOpen(false);
                setSwapAdId("");
                load();
            } else {
                setError(data.message || "Could not change the ad.");
            }
        } catch {
            setError("Network error. Nothing was changed.");
        }
        setBusy(false);
    }

    async function withdrawPending() {
        setBusy(true);
        setError(""); setNotice("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/campaigns/${campaignId}/creative`, {
                method: "DELETE", headers: authHeaders,
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setNotice(`${data.withdrawn} pending change${data.withdrawn === 1 ? "" : "s"} withdrawn.`);
                setSwapResult(null);
                load();
            } else {
                setError(data.message || "Could not withdraw those changes.");
            }
        } catch {
            setError("Network error. Nothing was changed.");
        }
        setBusy(false);
    }

    if (loading) return <p className="wb-sub">Loading campaign…</p>;
    if (!campaign) {
        return (
            <section className="wb-stack">
                <p className="wb-note wb-note--danger">{error || "That campaign was not found."}</p>
                <Link to="/customer/campaigns" className="wb-btn wb-btn--secondary wb-btn--sm">
                    Back to campaigns
                </Link>
            </section>
        );
    }

    const status = STATUS[campaign.status] || { label: campaign.status, tone: "" };
    const now = campaign.now || {};
    const w = campaign.window;
    const rollout = campaign.creative && campaign.creative.rollout;
    const screens = campaign.screens_detail || [];
    const visible = showAll ? screens : screens.slice(0, VISIBLE_SCREENS);
    const adName = (id) => {
        const ad = ads.find((a) => a.id === id);
        return ad ? (ad.image_metadata?.advertisement_name || ad.filename) : `Ad #${id}`;
    };

    return (
        <section className="wb-stack">
            <Link to="/customer/campaigns" className="wb-btn wb-btn--ghost wb-btn--sm">
                ← All campaigns
            </Link>

            {error && <p className="wb-note wb-note--danger">{error}</p>}
            {notice && <p className="wb-note wb-note--ok">{notice}</p>}

            <div className="wb-card wb-card--flush">
                <div className="camp-card__head">
                    {renaming ? (
                        <div className="camp-title-row">
                            <input
                                className="wb-input"
                                maxLength={120}
                                value={draftName}
                                autoFocus
                                onChange={(e) => setDraftName(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && saveName()}
                            />
                            <button className="wb-btn wb-btn--primary wb-btn--sm" disabled={busy} onClick={saveName}>
                                Save
                            </button>
                            <button className="wb-btn wb-btn--ghost wb-btn--sm" onClick={() => setRenaming(false)}>
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <div className="camp-title-row">
                            <h2 className="camp-title">{campaign.name}</h2>
                            <button
                                className="wb-btn wb-btn--ghost wb-btn--sm"
                                onClick={() => { setDraftName(campaign.name); setRenaming(true); }}
                            >
                                Rename
                            </button>
                        </div>
                    )}
                    <span className={"wb-badge" + (status.tone ? ` wb-badge--${status.tone}` : "")}>
                        {status.label}
                    </span>
                </div>

                <div className="camp-stats">
                    <div className="camp-stat">
                        <p className="camp-stat__label">Screens</p>
                        <p className="camp-stat__value">{campaign.screens}</p>
                    </div>
                    <div className="camp-stat">
                        <p className="camp-stat__label">Locations</p>
                        <p className="camp-stat__value">{campaign.locations}</p>
                    </div>
                    <div className="camp-stat">
                        <p className="camp-stat__label">Spend</p>
                        <p className="camp-stat__value">{money(campaign.money?.charged_cents)}</p>
                    </div>
                </div>

                {w && (
                    <div className="camp-progress">
                        <div className="camp-progress__head">
                            <strong>
                                {w.day ? `Day ${w.day} of ${w.days}` : `Starts ${dayMonth(w.starts_at)}`}
                            </strong>
                            <span>Ends {dateTime(w.ends_at)}</span>
                        </div>
                        <div className="camp-bar">
                            <div
                                className="camp-bar__fill"
                                style={{ width: `${Math.round((w.elapsed_fraction || 0) * 100)}%` }}
                            />
                        </div>
                    </div>
                )}

                {visible.map((s) => {
                    const state = screenState(s.now && s.now.state);
                    const detail = stateDetail(s.now);
                    return (
                        <div className="camp-screen" key={s.request_id}>
                            <div>
                                <div className="camp-screen__name">
                                    {s.device_name || `Screen #${s.device_id}`}
                                    {s.screen_code && (
                                        <span className="camp-screen__meta"> · {s.screen_code}</span>
                                    )}
                                </div>
                                <div className="camp-screen__meta">
                                    {[s.placement_label, s.location].filter(Boolean).join(" · ") || "—"}
                                </div>
                                {s.fit && s.fit.fit && s.fit.fit !== "good" && s.fit.fit !== "unknown" && (
                                    <div className={`camp-screen__detail camp-health--${FIT_TONE[s.fit.fit] === "danger" ? "issue" : "quiet"}`}>
                                        {s.fit.message}
                                    </div>
                                )}
                                {s.latest_creative_change && s.latest_creative_change.status === "pending" && (
                                    <div className="camp-screen__detail">
                                        New ad waiting for this host to approve it
                                    </div>
                                )}
                                {s.latest_creative_change && s.latest_creative_change.status === "rejected" && (
                                    <div className="camp-screen__detail camp-health--issue">
                                        This host declined your new ad; the original is still running
                                    </div>
                                )}
                            </div>
                            <div className="camp-screen__right">
                                <span className={"wb-badge" + (state.tone ? ` wb-badge--${state.tone}` : "")}>
                                    <span className={`camp-dot camp-dot--${state.dot}`} />
                                    {state.label}
                                </span>
                                {detail && <div className="camp-screen__detail">{detail}</div>}
                            </div>
                        </div>
                    );
                })}

                {screens.length > VISIBLE_SCREENS && (
                    <div className="camp-screen">
                        <button className="wb-btn wb-btn--ghost wb-btn--sm" onClick={() => setShowAll((v) => !v)}>
                            {showAll
                                ? "Show fewer"
                                : `+${screens.length - VISIBLE_SCREENS} more screen${screens.length - VISIBLE_SCREENS === 1 ? "" : "s"}`}
                        </button>
                    </div>
                )}

                <div className={
                    "camp-health " + (now.healthy === true ? "camp-health--ok"
                        : now.healthy === false ? "camp-health--issue" : "camp-health--quiet")
                }>
                    <span>{now.healthy === true ? "✓" : now.healthy === false ? "!" : "·"}</span>
                    <span>{now.message || "Nothing scheduled right now"}</span>
                    {/* Said on the page, not just in the payload: this is what the
                        schedule and the last check-in imply, not a photograph. */}
                    <span className="camp-derived">
                        Worked out from each screen's check-ins, not a live photo
                    </span>
                </div>
            </div>

            {/* --- the creative ------------------------------------------------ */}
            <div className="wb-card wb-stack">
                <div className="wb-page-head">
                    <h3 style={{ margin: 0 }}>The ad</h3>
                    {!swapOpen && (
                        <button className="wb-btn wb-btn--secondary wb-btn--sm" onClick={() => setSwapOpen(true)}>
                            Change ad on all screens
                        </button>
                    )}
                </div>

                {campaign.creative?.mixed ? (
                    <p className="wb-sub">This campaign is running more than one ad.</p>
                ) : campaign.creative?.image_id ? (
                    <p className="wb-sub">Running {adName(campaign.creative.image_id)}.</p>
                ) : null}

                {rollout && (
                    <p className="wb-note wb-note--warn">
                        {rollout.message}. Each host approves the new ad for their own screen,
                        so it goes up as they answer.
                        <button
                            className="wb-btn wb-btn--ghost wb-btn--sm"
                            style={{ marginLeft: "var(--s-3)" }}
                            disabled={busy || !rollout.awaiting_review}
                            onClick={withdrawPending}
                        >
                            Withdraw pending changes
                        </button>
                    </p>
                )}

                {swapOpen && (
                    <div className="wb-row" style={{ gap: "var(--s-3)", flexWrap: "wrap" }}>
                        <select
                            className="wb-select"
                            value={swapAdId}
                            onChange={(e) => setSwapAdId(e.target.value)}
                        >
                            <option value="">Choose an ad…</option>
                            {ads.map((ad) => (
                                <option key={ad.id} value={ad.id}>
                                    {ad.image_metadata?.advertisement_name || ad.filename}
                                </option>
                            ))}
                        </select>
                        <button className="wb-btn wb-btn--primary wb-btn--sm" disabled={busy} onClick={swapCreative}>
                            {busy ? "Requesting…" : "Request change"}
                        </button>
                        <button className="wb-btn wb-btn--ghost wb-btn--sm" onClick={() => setSwapOpen(false)}>
                            Cancel
                        </button>
                        <p className="wb-hint" style={{ width: "100%" }}>
                            Screens a host has already approved keep showing the current ad
                            until that host approves the new one. Nothing is charged.
                        </p>
                    </div>
                )}

                {swapResult && (
                    <div className="wb-stack">
                        {swapResult.fit_warning && (
                            <p className="wb-note wb-note--warn">{swapResult.fit_warning}</p>
                        )}
                        <ul className="wb-sub" style={{ margin: 0, paddingLeft: "1.1rem" }}>
                            {(swapResult.screens || []).map((s) => (
                                <li key={s.request_id}>
                                    {s.device_name || `Screen #${s.request_id}`} —{" "}
                                    {{
                                        applied: "switched now",
                                        awaiting_review: "waiting for the host",
                                        unchanged: "already showing it",
                                        not_changeable: "finished or cancelled, left alone",
                                    }[s.outcome] || s.outcome}
                                    {s.fit && s.fit.fit === "poor" && ` — ${s.fit.message}`}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* --- money ------------------------------------------------------- */}
            <div className="wb-card">
                <h3 style={{ marginTop: 0 }}>Money</h3>
                <p className="wb-sub">
                    {money(campaign.money?.charged_cents)} charged
                    {campaign.money?.refunded_cents > 0 && `, ${money(campaign.money.refunded_cents)} refunded`}
                    {campaign.booked_hours > 0 && ` · ${campaign.booked_hours} screen-hours approved`}
                </p>
                {campaign.money?.refunds_outstanding > 0 && (
                    <p className="wb-note wb-note--warn">
                        {campaign.money.refunds_outstanding} refund
                        {campaign.money.refunds_outstanding === 1 ? " is" : "s are"} still being
                        settled. Nothing more is owed from you.
                    </p>
                )}
            </div>
        </section>
    );
}
